/** What the scan row alone can say about a failed or partial scan (DEC-197). */
import type { ScanStatusResponse, WhatHappenedAgent, WhatHappenedResponse, WhatHappenedStep } from '../../types/api'
import { AGENT_LABELS } from '../../types/api'

export const NOT_RECORDED_LINE =
  'Details like run time and the technical cause were not recorded for scans before 18 Oct 2026.'

/** "d5_security: boom" -> ["d5_security", "boom"]; anything else belongs to the scan. */
function splitError(line: string): [string | null, string] {
  const m = /^([a-z][a-z0-9_]*):\s(.*)$/s.exec(line)
  if (m && AGENT_LABELS[m[1]]) return [m[1], m[2]]
  return [null, line]
}

/**
 * The story from the scan row: against a server without GET /what-happened. Every saved
 * `errors` line, grouped by the agent it names, and nothing that was not recorded.
 */
export function fromScanRow(scan: ScanStatusResponse): WhatHappenedResponse {
  const byAgent = new Map<string, string[]>()
  const scanErrors: string[] = []
  for (const line of scan.errors ?? []) {
    const [agent, msg] = splitError(line)
    if (agent) byAgent.set(agent, [...(byAgent.get(agent) ?? []), msg])
    else scanErrors.push(line)
  }
  const failed = new Set(scan.agents_failed ?? [])
  const failures = new Map((scan.agent_failures ?? []).map((f) => [f.agent_id, f]))
  const agents: WhatHappenedAgent[] = [...byAgent.entries()].map(([agent_id, errors]) => ({
    agent_id,
    label: AGENT_LABELS[agent_id] ?? agent_id,
    status: failed.has(agent_id) ? 'failed' : 'done_with_problems',
    errors,
    problems: [],
    failure: failures.get(agent_id) ?? null,
  }))
  const timeline: WhatHappenedStep[] = [{ kind: 'started', label: 'Started', at: scan.started_at, status: 'ok' }]
  if (scan.completed_at) {
    const isFailed = scan.state === 'failed'
    timeline.push({
      kind: isFailed ? 'failed' : 'finished',
      label: isFailed ? 'Stopped here' : 'Finished',
      at: scan.completed_at,
      status: isFailed ? 'failed' : scan.state === 'partial' ? 'problems' : 'ok',
      detail: scan.failure_message ?? null,
    })
  }
  return {
    scan_id: scan.scan_id,
    state: scan.state,
    summary:
      scan.failure_message ??
      (scan.state === 'partial'
        ? 'The scan finished, but some agents could not run, so the results leave those areas out.'
        : 'The scan did not finish.'),
    timeline,
    agents,
    scan_errors: scanErrors,
    failure_code: scan.failure_code,
    failure_message: scan.failure_message,
    failure_remedy: scan.failure_remedy,
    failure_is_retryable: scan.failure_is_retryable,
    run_seconds: scan.run_seconds ?? null,
    run_time_is_partial: !!scan.run_time_is_partial,
    recorded_details: false,
  }
}
